import React, { useMemo } from 'react';

import { type UseQueryOptions, useQuery } from '@lumapps/base-api/react-query';

import { marketplaceConnectorsQueryKeys } from '../api';
import { getConnectorAuthStatus } from '../api/connectorsApi';
import type { GetConnectorDTO } from '../types';

/**
 * Hook to get a single connector's authorization status
 *
 * @param {GetConnectorDTO} params
 * @param {UseQueryOptions | undefined} options
 * @returns {useGetConnectorAuthStatus}
 */
export const useGetConnectorAuthStatus = ({ connectorId, onError }: GetConnectorDTO, options?: UseQueryOptions) => {
    const query = useQuery({
        ...options,
        queryKey: marketplaceConnectorsQueryKeys.connectorAuthStatus(connectorId),
        queryFn: async ({ signal }) => getConnectorAuthStatus(connectorId, signal),
    });

    /**
     * If the call ends in an error, we want to execute the error callback.
     */
    React.useEffect(() => {
        if (query.isError && onError) {
            onError();
        }
    }, [onError, query.isError]);

    const isConnected = useMemo(() => query.data?.connected, [query.data]);

    return { isConnected, ...query };
};
